import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  doc,
  collection,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { AppDispatch } from "./store";

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */

export interface DailyRevenue {
  date: string; // maps from dateKey
  generalRevenue: number;
}

interface RevenueState {
  totalRevenue: number;
  generalCommerceRevenue: number;
  dailyRevenue: DailyRevenue[];
  loading: boolean;
}

const initialState: RevenueState = {
  totalRevenue: 0,
  generalCommerceRevenue: 0,
  dailyRevenue: [],
  loading: false,
};

/* ─────────────────────────────────────────────
   SLICE
───────────────────────────────────────────── */

const revenueSlice = createSlice({
  name: "revenue",
  initialState,
  reducers: {
    setRevenueRoot: (
      state,
      action: PayloadAction<{
        totalRevenue: number;
        generalCommerceRevenue: number;
      }>
    ) => {
      state.totalRevenue = action.payload.totalRevenue || 0;
      state.generalCommerceRevenue =
        action.payload.generalCommerceRevenue || 0;
    },

    setDailyRevenue: (
      state,
      action: PayloadAction<DailyRevenue[]>
    ) => {
      state.dailyRevenue = action.payload || [];
    },

    clearRevenue: (state) => {
      state.totalRevenue = 0;
      state.generalCommerceRevenue = 0;
      state.dailyRevenue = [];
    },
  },
});

export const {
  setRevenueRoot,
  setDailyRevenue,
  clearRevenue,
} = revenueSlice.actions;

/* ─────────────────────────────────────────────
   REALTIME LISTENERS (MATCHING BACKEND)
───────────────────────────────────────────── */

/**
 * ✅ Listen to root revenue document
 * Backend path: revenue/{userId}
 */
export const listenToPlatformRevenueRoot =
  (userId: string) => (dispatch: AppDispatch) => {
    const revenueRef = doc(db, "revenue", userId);

    const unsubscribe = onSnapshot(
      revenueRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();

          dispatch(
            setRevenueRoot({
              totalRevenue: data.totalRevenue || 0,
              generalCommerceRevenue:
                data.generalCommerceRevenue || 0,
            })
          );
        } else {
          dispatch(clearRevenue());
        }
      },
      (error) => {
        console.error("Revenue root listener error:", error);
      }
    );

    return unsubscribe;
  };

/**
 * ✅ Listen to daily revenue subcollection
 * Backend path: revenue/{userId}/revenue/{dateKey}
 */
export const listenToPlatformDailyRevenue =
  (userId: string) => (dispatch: AppDispatch) => {
    const dailyRef = collection(
      db,
      "revenue",
      userId,
      "revenue"
    );

    const q = query(dailyRef, orderBy("dateKey", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const dailyData: DailyRevenue[] =
          snapshot.docs.map((doc) => ({
            date: doc.data().dateKey, // 🔥 matches backend
            generalRevenue:
              doc.data().generalRevenue || 0,
          }));

        dispatch(setDailyRevenue(dailyData));
      },
      (error) => {
        console.error(
          "Daily revenue listener error:",
          error
        );
      }
    );

    return unsubscribe;
  };

export default revenueSlice.reducer;
